/**
 * Office time clock for hourly office users (an Admin ticks "Tracks hours" on the user). Not
 * tied to a job: Clock In, Out for Lunch, Clock Out. The server records server time.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ClockStatus } from '@wo/shared';
import { api, errorText } from '../lib/api';
import { time } from '../lib/format';

export function OfficeClock() {
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ['time-clock'], queryFn: () => api.get<ClockStatus>('/api/time-clock/me'), refetchInterval: 60_000 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const s = status.data;
  if (!s) return null;

  const call = async (url: string, body: object) => {
    setBusy(true);
    setError(null);
    try {
      qc.setQueryData(['time-clock'], await api.post<ClockStatus>(url, body));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const btn = 'rounded-md px-3 py-1.5 text-sm font-semibold disabled:opacity-50';
  const on = s.state === 'time' || s.state === 'job';

  return (
    <span className="flex items-center gap-2" title={error ?? undefined}>
      <span className={on ? 'font-semibold text-green-300' : 'text-slate-300'}>
        {on ? `Clocked in since ${time(s.since!)}` : s.state === 'lunch' ? `At lunch since ${time(s.lastOutAt!)}` : 'Clocked out'}
        {s.hoursToday > 0 && ` · ${s.hoursToday.toFixed(2)} h today`}
      </span>
      {on ? (
        <>
          <button className={`${btn} bg-amber-400 text-black`} disabled={busy} onClick={() => call('/api/time-clock/out', { reason: 'LUNCH' })}>Out for Lunch</button>
          <button className={`${btn} bg-red-600 text-white`} disabled={busy} onClick={() => call('/api/time-clock/out', { reason: 'DAY' })}>Clock Out</button>
        </>
      ) : (
        <button className={`${btn} bg-green-600 text-white`} disabled={busy} onClick={() => call('/api/time-clock/in', {})}>Clock In</button>
      )}
      {error && <span className="text-xs text-red-300">{error}</span>}
    </span>
  );
}
